import { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { loadEvents } from '../services/api'
import EventCard from '../components/EventCard'
import './Home.css'

const Home = ({ eventType: propEventType }) => {
  const location = useLocation()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortBy, setSortBy] = useState('date')
  
  // Get event type from prop, location path, or default to 'all'
  const getEventType = () => {
    if (propEventType) return propEventType
    const path = location.pathname
    if (path === '/school') return 'school'
    if (path === '/seminar') return 'seminar'
    if (path === '/workshop') return 'workshop'
    return 'all'
  }
  
  const eventType = getEventType()

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      const data = await loadEvents(eventType)
      setEvents(data)
      setLoading(false)
    }
    fetchData()
  }, [eventType])

  // Filter and sort events
  const filteredAndSortedEvents = useMemo(() => {
    let filtered = [...events]

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(event => 
        event.title.toLowerCase().includes(query) ||
        event.subtitle.toLowerCase().includes(query) ||
        event.overview?.toLowerCase().includes(query)
      )
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(event => 
        event.status.toLowerCase() === statusFilter.toLowerCase()
      )
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a.title.localeCompare(b.title)
        case 'date':
          // Simple date comparison (you might want to improve this)
          return a.date.localeCompare(b.date)
        case 'type':
          return a.type.localeCompare(b.type)
        default:
          return 0
      }
    })

    return filtered
  }, [events, searchQuery, statusFilter, sortBy])

  // Calculate statistics
  const stats = useMemo(() => {
    const total = events.length
    const upcoming = events.filter(e => e.status.toLowerCase() === 'upcoming').length
    const ongoing = events.filter(e => e.status.toLowerCase() === 'ongoing').length
    const completed = events.filter(e => e.status.toLowerCase() === 'completed').length
    
    return { total, upcoming, ongoing, completed }
  }, [events])

  return (
    <>
      {/* Enhanced Hero Section */}
      <section className="hero-bg">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">ITCPR Events Platform</div>
            <h1>Discover Physics Events</h1>
            <p className="hero-subtitle">
              Explore cutting-edge seminars, public talks, and schools organized by the Institute for Theoretical and Computational Physics Research.
            </p>
            
            {/* Statistics */}
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-number">{stats.total}</div>
                <div className="stat-label">Total Events</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{stats.upcoming}</div>
                <div className="stat-label">Upcoming</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{stats.ongoing}</div>
                <div className="stat-label">Ongoing</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{stats.completed}</div>
                <div className="stat-label">Completed</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters and Search Section */}
      <section className="filters-section">
        <div className="container">
          <div className="filters-container">
            {/* Search and Filters */}
            <div className="search-filters-row">
              <div className="search-box">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  placeholder="Search events by title, description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button 
                    className="clear-search"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>

              <div className="filter-controls">
                <select
                  className="filter-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                </select>

                <select
                  className="filter-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="date">Sort by Date</option>
                  <option value="title">Sort by Title</option>
                  <option value="type">Sort by Type</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="events-section">
        <div className="container">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading events...</p>
            </div>
          ) : filteredAndSortedEvents.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <i className="fas fa-calendar-times"></i>
              </div>
              <h3>No events found</h3>
              <p>
                {searchQuery || statusFilter !== 'all'
                  ? "Try adjusting your search or filters to find more events."
                  : "No events available at this time. Check back soon!"}
              </p>
              {(searchQuery || statusFilter !== 'all') && (
                <button 
                  className="clear-filters-btn"
                  onClick={() => {
                    setSearchQuery('')
                    setStatusFilter('all')
                  }}
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <div className="events-grid">
              {filteredAndSortedEvents.map(event => (
                <EventCard key={`${event.type}-${event.id}`} event={event} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  )
}

export default Home
